import { useIntl } from '@jetshop/intl';
import { css, cx } from 'linaria';
import { styled } from 'linaria/react';
import React, { useState } from 'react';
import { validateNumber } from '../../utils/HelpFunctions';
import ButtonWithLoading from '../ui/Button';
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItem,
  DropdownMenuItems
} from '@jetshop/ui/DropdownMenu';
import { Popup } from './Popup';
import StockOrb from './StockStatus/StockOrb';
import { theme } from '../Theme';
import { ReactComponent as Caret } from '../../svg/Caret.svg';
import { ReactComponent as Check } from '../../svg/Check.svg';

export function VariantSelector({
  product,
  variantHandler,
  showValidation,
  disableOutOfStock,
  isDropdown
}) {
  return (
    <div className={sharedStyles}>
      {product.variants.options.map((option, index) => {
        let showValidationMessage = false;
        if (
          showValidation &&
          variantHandler.getMissingOptions()?.includes(option)
        ) {
          showValidationMessage = true;
        }

        return (
          <>
            {isDropdown ? (
              <DropdownVariantSelect
                key={option.name}
                option={option}
                variantHandler={variantHandler}
                showValidationMessage={showValidationMessage}
                doNotDisable={index === 0}
              />
            ) : (
              <ButtonSelect
                option={option}
                variantHandler={variantHandler}
                key={option.name}
                showValidationMessage={showValidationMessage}
                disableOutOfStock={disableOutOfStock}
                doNotDisable={index === 0}
                product={product}
              />
            )}
          </>
        );
      })}
    </div>
  );
}

const sharedStyles = css`
  label {
    display: flex;
    padding-bottom: 0.25em;
    margin-top: 1em;
    font-size: 0.875em;
    align-items: center;
  }
  .invalid {
    label {
      color: #eb5757;
    }
  }
  .missingVariant {
    margin-right: 0.25em;
    height: 10px;
    width: 10px;
  }
`;

const VariantsRowWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 6px;
  padding-left: 6px;

  .size-guide {
    /* margin-right: 6px; */
    font-weight: 400;
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }

  label {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    text-align: right;
    color: #333333;
  }
`;

function ButtonSelect({
  option,
  variantHandler,
  showValidationMessage,
  disableOutOfStock,
  doNotDisable,
  product
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { getSelectedValue, validateSelection, selectValue } = variantHandler;
  const selectedValue = getSelectedValue(option);
  const t = useIntl();
  const sizeguideId =
    validateNumber(
      product.customFields.find(({ key }) => key === 'size_guide_cat_id')
        ?.stringValue
    ) ?? 581;

  //******** TEXT TRANSLATION VARIABLES ************
  const translate = useIntl();
  const sizeGuideText = translate('Size guide');
  //************************************************

  return (
    <div className={cx(buttonSelectStyles, showValidationMessage && 'invalid')}>
      <VariantsRowWrapper className="variantsrow-wrapper">
        <label>
          {showValidationMessage && <StockOrb className="missingVariant" />}
          {t('Select size')}
        </label>
        {sizeguideId && (
          <>
            <label
              onClick={() => {
                setIsOpen(true);
              }}
              className={'size-guide'}
              style={{ textDecoration: 'underline' }}
            >
              {sizeGuideText}
            </label>
            <Popup
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              categoryId={sizeguideId}
            />
          </>
        )}
      </VariantsRowWrapper>
      <div className="button-variant-select">
        {option.values.map(value => {
          const validation = validateSelection(value, option);
          return (
            <ButtonWithLoading
              data-testid={option.name + value}
              key={option.name + value}
              onClick={() => selectValue(value, option)}
              className={
                validation === 'outOfStock' ? 'out-of-stock' : 'in-stock'
              }
              disabled={
                !doNotDisable &&
                (validation === 'invalid' ||
                  (disableOutOfStock && validation === 'outOfStock'))
              }
              secondary={value !== selectedValue}
              aria-pressed={value === selectedValue}
            >
              {value}
            </ButtonWithLoading>
          );
        })}
      </div>
    </div>
  );
}

const buttonSelectStyles = css`
  /* margin: 1em -6px 2px -6px;
  width: calc(100% + 6px); */
  button {
    width: auto;
    min-width: unset;
    height: 40px;
    padding: 0 20px;
    margin-bottom: 6px;
    font-weight: 500;
    margin-left: 6px;

    &.out-of-stock {
      color: #ddd;
    }
  }

  > * {
    margin-left: -6px;
  }
  &.invalid button {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
`;

function DropdownVariantSelect({
  option,
  variantHandler,
  showValidationMessage,
  doNotDisable
}) {
  const { getSelectedValue, validateSelection, selectValue } = variantHandler;
  const t = useIntl();

  const selectedValue = getSelectedValue(option);

  console.log('name', option?.name);

  return (
    <div className={cx(dropdownStyles, showValidationMessage && 'invalid')}>
      <label htmlFor={`option-${option.name}`}>
        {showValidationMessage && <StockOrb className="missingVariant" />}
        Velg {option.name}
      </label>
      <DropdownMenu>
        <DropdownMenuButton id={`option-${option.name}`}>
          {selectedValue
            ? selectedValue
            : t.rich('{option}', { option: option.name })}
          <Caret />
        </DropdownMenuButton>
        <DropdownMenuItems style={{ zIndex: 9999 }}>
          {option.values.map(value => {
            const validation = validateSelection(value, option);
            return (
              <DropdownMenuItem
                data-testid={value + option.name}
                key={value + option.name}
                disabled={!doNotDisable && validation === 'invalid'}
                onSelect={({ setIsOpen }) => {
                  selectValue(value, option);
                  setIsOpen(false);
                }}
                style={{ opacity: validation === 'invalid' ? 0.5 : 1 }}
              >
                {value}
                {selectedValue === value && <Check />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuItems>
      </DropdownMenu>
    </div>
  );
}

export const dropdownStyles = css`
  // && wrapping needed to avoid non-deterministic styling
  && {
    label {
      width: 100%;
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 4px;
    }

    margin: 10px 0;
    [data-flight-dropdown] {
      height: 40px;
      background: #fcfcfc;
    }
    button {
      height: 40px;
      font-family: ${theme.fonts.primary};
      font-weight: 400;
      font-size: 12px;
      line-height: 18px;
      letter-spacing: 0.02em;
      color: #333;
    }
    [data-flight-dropdown-button] {
      background: #fcfcfc;
      width: 100%;
      display: flex;
      align-items: center;
      border-radius: 0;
      border: 1px solid #e8e8e8;
    }
    [data-flight-dropdown-open='true'] {
      [data-flight-dropdown-button] {
      }
    }
    [data-flight-dropdown-items] {
      width: 100%;
      border: 1px solid #e8e8e8;
      margin-top: -1px;
      background: #fcfcfc;
    }
    [data-flight-dropdown-item] {
      height: 38px;
      display: flex;
      align-items: center;
      font-family: ${theme.fonts.primary};
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: 0.02em;
      label {
        width: 100%;
      }
      &:hover {
        color: ${theme.colors.gold};
      }

      svg {
        margin-left: auto;
        fill: ${theme.colors.gold} !important;
      }
    }
    &.invalid [data-flight-dropdown-button] {
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    }
    svg {
      margin-left: auto;
    }
  }
`;
